/**
 * Wire types shared by every consumer of the auth Worker's `/friends/*` and
 * `/invites/*` routes. These mirror the payload shapes in
 * `workers/auth/src/handlers/` — that handler code is the authority.
 */

/**
 * WebSocket namespace slugs, as registered in `server/src/index.ts` and
 * validated by `POST /invites`. Note the slug is not always the web path:
 * backgammon lives at `/long-backgammon/`, so slug → URL is the caller's job.
 */
export const ONLINE_GAMES = ['backgammon', 'checkers', 'domino', 'mahjong', 'chess'] as const;

export type OnlineGame = (typeof ONLINE_GAMES)[number];

export function isOnlineGame(value: unknown): value is OnlineGame {
  return typeof value === 'string' && (ONLINE_GAMES as readonly string[]).includes(value);
}

/**
 * The one audience vocabulary behind `presence_consent` and `invites_from`,
 * owned by `auth-client` (it lands in the browser via `GET /auth/me`) and
 * re-exported here so both packages speak from a single definition.
 */
export type { PrivacyScope } from '@bn-games/auth-client';

export interface Friend {
  userId: string;
  handle: string | null;
  displayName: string;
  avatarUrl: string;
}

/** A search hit also reports whether the caller already follows this person. */
export interface FriendSearchResult extends Friend {
  following: boolean;
}

export type InviteStatus = 'pending' | 'accepted' | 'declined';

/** What the sender gets back from `POST /invites` — no sender identity: theirs. */
export interface SentInvite {
  id: string;
  game: OnlineGame;
  roomCode: string;
  status: InviteStatus;
  createdAt: number;
  expiresAt: number;
}

/** A received invite always carries its sender — the recipient needs the name. */
export interface Invite extends SentInvite {
  from: Friend;
}

/** What `POST /invites/accept` hands back: enough to join the existing room. */
export interface AcceptedInvite {
  game: OnlineGame;
  roomCode: string;
}

export type PresenceState = 'online' | 'in_game' | 'offline';

/**
 * Live presence for one user, populated over the `/ws/lobby` socket
 * (`LobbySocket`'s `onPresence`, milestone 2). A friend with no entry yet —
 * before the first `PRESENCE_QUERY` answer arrives, or one whose consent
 * setting never authorized this viewer — should be treated as `offline`.
 */
export interface PresenceStatus {
  userId: string;
  state: PresenceState;
  /** Set only when `state` is `'in_game'`. */
  game: OnlineGame | null;
  /** Epoch ms of the last known activity, or null when never seen. */
  lastSeenAt: number | null;
}

/**
 * The persistent `/ws/lobby` channel's wire messages (T17), re-exported here
 * so `LobbySocket` (T19) and its consumers pull everything from this
 * package without reaching into `@bn-games/network-protocol` directly.
 */
export type {
  LobbyHelloMessage,
  PresenceQueryMessage,
  LobbyPresenceEntry,
  PresenceStateMessage,
  InviteSendMessage,
  InvitePushMessage,
  LobbyChannelClientMessage,
  LobbyChannelServerMessage,
} from '@bn-games/network-protocol';
